import { PROTOCOL_VERSION, isWelcome, transferables } from "./protocol";
import type { HelloMessage, HostEventName, HostOp, HostSession, HostToPage, PageToHost } from "./protocol";
import type { PageBoot } from "./types";

/** A refused or failed platform call: `code` is the capability error (forbidden, ungranted, limited, invalid, session, …). */
export class NcpError extends Error {
  readonly code: string;
  readonly status: number | undefined;

  constructor(code: string, message: string, status?: number) {
    super(message);
    this.name = "NcpError";
    this.code = code;
    this.status = status;
  }
}

const FALLBACK_BOOT: PageBoot = {
  key: "page", version: 0, entry: "index.html", entries: ["index.html"], locale: "en", dir: "ltr", theme: "ncp", space: null, slot: null,
  matterId: null, draft: false, assets: null, vendor: "/api/pages/vendor/", tiles: "/api/pages/tiles/", built: false, origin: null,
};

export const boot: PageBoot = { ...FALLBACK_BOOT, ...((globalThis as { __NCP_PAGE__?: Partial<PageBoot> }).__NCP_PAGE__ ?? {}) };

type Pending = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  onChunk?: (chunk: unknown) => void;
};

export type ToolHandler = (args: never) => unknown | Promise<unknown>;

/**
 * The workspace host's stand-in for the bridge when page-kit runs in the host itself (UI blocks in the copilot's answers): calls go to the
 * open page's session, client tools to its frame, host operations to the workspace.
 */
export interface PageEmbed {
  locale: () => string;
  call: (capability: string, input: unknown, signal?: AbortSignal) => Promise<unknown>;
  invokeTool: (name: string, args: unknown) => Promise<unknown>;
  host: (op: HostOp, args: unknown) => Promise<unknown>;
}

const HANDSHAKE_MS = 15_000;

/**
 * The page's end of the bridge. Every platform call goes to the host over a private MessagePort; the host adds the user's sign-in
 * and the page token and calls the capability registry, so page code never holds a credential.
 */
class PageBridge {
  private port: MessagePort | null = null;
  private seq = 0;
  private readonly pending = new Map<number, Pending>();
  private readonly tools = new Map<string, ToolHandler>();
  private readonly listeners = new Map<string, Set<(payload: unknown) => void>>();
  private sessionValue: HostSession | null = null;
  private embedded: PageEmbed | null = null;
  readonly session: Promise<HostSession>;

  constructor() {
    this.session = this.connect();
    this.session.then((s) => {
      this.sessionValue = s;
    }, () => undefined);
  }

  get current(): HostSession | null {
    return this.sessionValue;
  }

  get locale(): string {
    return this.embedded?.locale() ?? this.sessionValue?.locale ?? boot.locale;
  }

  embed(host: PageEmbed): void {
    this.embedded = host;
  }

  private notInHost(): never {
    throw new NcpError("standalone", "This runs inside a custom page, not in the workspace.");
  }

  private connect(): Promise<HostSession> {
    return new Promise((resolve, reject) => {
      if (typeof window === "undefined" || window.parent === window) {
        reject(new NcpError("standalone", "This page runs inside the NCP platform. Open it from its space."));
        return;
      }
      const nonce = /\/api\/pages\/run\/([^/]+)\//.exec(window.location.pathname)?.[1] ?? null;
      const instance = Math.random().toString(36).slice(2) + Date.now().toString(36);
      const hello: HelloMessage = { ncp: PROTOCOL_VERSION, kind: "hello", page: boot.key, version: boot.version, entry: boot.entry, nonce, instance };
      const send = () => window.parent.postMessage(hello, boot.origin ?? "*");
      const onMessage = (e: MessageEvent) => {
        if (e.source !== window.parent || !isWelcome(e.data) || !e.ports[0]) return;
        if (boot.origin && e.origin !== boot.origin) return;
        window.removeEventListener("message", onMessage);
        clearInterval(timer);
        clearTimeout(timeout);
        this.port = e.ports[0];
        this.port.onmessage = (m: MessageEvent<HostToPage>) => this.receive(m.data);
        this.port.start();
        resolve(e.data.session);
      };
      window.addEventListener("message", onMessage);
      // The host may attach its listener after the frame has started, so the greeting repeats until it is answered.
      const timer = setInterval(send, 300);
      const timeout = setTimeout(() => {
        clearInterval(timer);
        window.removeEventListener("message", onMessage);
        reject(new NcpError("handshake", "The platform did not answer the page. Reload the page."));
      }, HANDSHAKE_MS);
      send();
    });
  }

  private post(message: PageToHost): void {
    if (!this.port) throw new NcpError("handshake", "The page is not connected to the platform yet.");
    this.port.postMessage(message, transferables(message));
  }

  private receive(message: HostToPage): void {
    switch (message.kind) {
      case "result": {
        const p = this.pending.get(message.id);
        this.pending.delete(message.id);
        p?.resolve(message.result);
        break;
      }
      case "error": {
        const p = this.pending.get(message.id);
        this.pending.delete(message.id);
        p?.reject(new NcpError(message.code ?? "error", message.error, message.status));
        break;
      }
      case "chunk":
        this.pending.get(message.id)?.onChunk?.(message.chunk);
        break;
      case "end": {
        const p = this.pending.get(message.id);
        this.pending.delete(message.id);
        p?.resolve(undefined);
        break;
      }
      case "tool":
        void this.runTool(message.callId, message.tool, message.arguments);
        break;
      case "event":
        this.listeners.get(message.name)?.forEach((fn) => fn(message.payload));
        this.listeners.get("*")?.forEach((fn) => fn({ name: message.name, payload: message.payload }));
        break;
    }
  }

  private async runTool(callId: string, tool: string, args: unknown): Promise<void> {
    const name = tool.startsWith("client_") ? tool.slice(7) : tool;
    const handler = this.tools.get(name) ?? this.tools.get(tool);
    try {
      if (!handler) throw new Error(`The page has no handler for "${name}". Register it with ncp.tools.register.`);
      const result = await (handler as (a: unknown) => unknown)(args ?? {});
      this.post({ kind: "tool-result", callId, result: result === undefined ? { ok: true } : result });
    } catch (e) {
      this.post({ kind: "tool-result", callId, error: e instanceof Error ? e.message : String(e) });
    }
  }

  private async open(build: (id: number) => PageToHost, onChunk?: (chunk: unknown) => void, signal?: AbortSignal): Promise<unknown> {
    await this.session;
    const id = ++this.seq;
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new NcpError("aborted", "Cancelled."));
        return;
      }
      this.pending.set(id, { resolve, reject, onChunk });
      signal?.addEventListener("abort", () => {
        if (!this.pending.has(id)) return;
        this.pending.delete(id);
        try {
          this.post({ kind: "cancel", id });
        } catch {
          // the port is gone; nothing to cancel
        }
        reject(new NcpError("aborted", "Cancelled."));
      }, { once: true });
      try {
        this.post(build(id));
      } catch (e) {
        this.pending.delete(id);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  call<T>(capability: string, input?: unknown, signal?: AbortSignal): Promise<T> {
    if (this.embedded) return this.embedded.call(capability, input ?? {}, signal) as Promise<T>;
    return this.open((id) => ({ kind: "call", id, capability, input }), undefined, signal) as Promise<T>;
  }

  arrow(capability: string, input: unknown, signal?: AbortSignal): Promise<ArrayBuffer> {
    if (this.embedded) return Promise.reject(new NcpError("standalone", "Arrow transfers run inside a custom page."));
    return this.open((id) => ({ kind: "arrow", id, capability, input }), undefined, signal) as Promise<ArrayBuffer>;
  }

  stream(capability: string, input: unknown, onChunk: (chunk: unknown) => void, signal?: AbortSignal): Promise<void> {
    if (this.embedded) return Promise.reject(new NcpError("standalone", "Streams run inside a custom page."));
    return this.open((id) => ({ kind: "stream", id, capability, input }), onChunk, signal) as Promise<void>;
  }

  chat(agent: string, message: string, threadId: string | null | undefined, state: unknown, onEvent: (event: unknown) => void, signal?: AbortSignal): Promise<void> {
    if (this.embedded) return Promise.reject(new NcpError("standalone", "Page agents are asked from their page."));
    return this.open((id) => ({ kind: "chat", id, agent, message, threadId, state }), onEvent, signal) as Promise<void>;
  }

  host<T>(op: HostOp, args?: unknown): Promise<T> {
    if (this.embedded) return this.embedded.host(op, args ?? {}) as Promise<T>;
    return this.open((id) => ({ kind: "host", id, op, args })) as Promise<T>;
  }

  ready(): void {
    if (this.embedded) return;
    void this.session.then(() => this.post({ kind: "ready" }), () => undefined);
  }

  on(name: HostEventName | "*", handler: (payload: unknown) => void): () => void {
    let set = this.listeners.get(name);
    if (!set) this.listeners.set(name, (set = new Set()));
    set.add(handler);
    return () => set!.delete(handler);
  }

  /** Runs one of the page's own client tools (a button in an agent's block calling the page directly). */
  async invokeTool(name: string, args: unknown): Promise<unknown> {
    if (this.embedded) return this.embedded.invokeTool(name, args ?? {});
    const handler = this.tools.get(name.startsWith("client_") ? name.slice(7) : name) ?? this.tools.get(name);
    if (!handler) throw new NcpError("not_found", `The page has no tool "${name}".`);
    return (handler as (a: unknown) => unknown)(args ?? {});
  }

  hasTool(name: string): boolean {
    return this.tools.has(name.startsWith("client_") ? name.slice(7) : name) || this.tools.has(name);
  }

  registerTools(handlers: Record<string, ToolHandler>): () => void {
    if (this.embedded) this.notInHost();
    for (const [name, fn] of Object.entries(handlers)) this.tools.set(name, fn);
    void this.host("tools", { names: [...this.tools.keys()] }).catch(() => undefined);
    return () => {
      for (const name of Object.keys(handlers)) if (this.tools.get(name) === handlers[name]) this.tools.delete(name);
      void this.host("tools", { names: [...this.tools.keys()] }).catch(() => undefined);
    };
  }
}

export const bridge = new PageBridge();
